import { useCallback, useEffect, useState } from 'react'
import {
  type ApiCbrRate,
  type ApiForexRate,
  type ApiMe,
  fetchCbrRate,
  fetchForexRate,
} from '@/fetcher'
import { SectionCard } from './SectionCard'

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const formatMoscowDateTime = (value: string | null | undefined) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : moscowDateTimeFormatter.format(date)
}

const formatRate = (value: number | string | null | undefined) => {
  const rate = typeof value === 'string' ? Number(value) : value
  return typeof rate === 'number' && Number.isFinite(rate)
    ? Number(rate.toFixed(4)).toString()
    : '—'
}

type RatesPageProps = {
  me: ApiMe
}

export const RatesPage = ({ me }: RatesPageProps) => {
  const [cbrRate, setCbrRate] = useState<ApiCbrRate | null>(null)
  const [forexRate, setForexRate] = useState<ApiForexRate | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const loadRates = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)
    try {
      const [cbr, forex] = await Promise.all([
        fetchCbrRate().catch(() => null),
        fetchForexRate().catch(() => null),
      ])
      setCbrRate(cbr)
      setForexRate(forex)
      if (!cbr && !forex) {
        setErrorMessage('Биржевые курсы временно недоступны')
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Не удалось загрузить курсы',
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRates()
  }, [loadRates])

  return (
    <div className="mx-auto max-w-2xl space-y-4 px-4 py-8">
      <SectionCard
        title="Биржевые курсы"
        footer={
          <button
            type="button"
            onClick={() => void loadRates()}
            disabled={isLoading}
            className="text-sm font-medium text-[var(--app-accent)] transition hover:opacity-80 disabled:opacity-50"
          >
            {isLoading ? 'Обновление...' : 'Обновить'}
          </button>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-3">
            <p className="text-xs text-[var(--app-muted)]">ЦБ</p>
            <p className="mt-1 text-lg font-semibold text-[var(--app-text)]">
              {formatRate(cbrRate?.rate)}
            </p>
            <p className="mt-1 text-xs text-[var(--app-muted)]">
              {formatMoscowDateTime(cbrRate?.updatedAt)}
            </p>
          </div>
          <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-3">
            <p className="text-xs text-[var(--app-muted)]">Forex</p>
            <p className="mt-1 text-lg font-semibold text-[var(--app-text)]">
              {formatRate(forexRate?.ask)}
            </p>
            <p className="mt-1 text-xs text-[var(--app-muted)]">
              {formatMoscowDateTime(forexRate?.updatedAt)}
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Курсы команд">
        <div className="divide-y divide-[var(--app-input)] rounded-[18px] bg-[var(--app-input)] px-4">
          {me.teams.map((team) => (
            <div
              key={team.id}
              className="flex items-center justify-between gap-4 py-3 first:pt-3 last:pb-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[var(--app-text)]">
                  {team.name}
                </p>
                <p className="mt-1 text-xs text-[var(--app-muted)]">
                  Обновлён: {formatMoscowDateTime(team.rateUpdatedAt)}
                </p>
              </div>
              <p className="shrink-0 text-lg font-semibold text-[var(--app-text)]">
                {formatRate(team.usdtSellRate)}
              </p>
            </div>
          ))}
        </div>
      </SectionCard>

      {errorMessage && (
        <div className="rounded-[18px] bg-[var(--app-danger-soft)] px-4 py-3 text-sm text-[var(--app-text)]">
          {errorMessage}
        </div>
      )}
    </div>
  )
}
